import {formatISO, isValid} from "date-fns";
import {getDecimal} from "../global/utils";
import {dateIso2Us} from "../global/dateUtils";

export const validateLookup = (form, court, dob) => {
    let errors = '';
    if (!form.caseNo && !form.citeNo && !form.licenseLookupNumber && !form.lastName) {
        if (court.allowNameSearch) {
            errors = court.licensePlateLookup ? 'A case number, citation number, name or license plate number is required' : 'A case number, citation number or name is required';
        } else errors = court.licensePlateLookup ? 'A case number, citation number, or license plate number is required' : 'A case or citation number is required';
    } else {
        if (form.caseNo && form.caseNo.length > 0) {
            if (court.minCaseLength && court.minCaseLength > 0 && court.minCaseLength > form.caseNo.length) {
                errors += `${errors.length > 0 ? '; ' : ''}The ${getCaseLabel(court)} must be at least ${court.minCaseLength} characters long`;
            }
        } else if (form.citeNo && form.citeNo.length > 0) {
            if (court.minCitationLength && court.minCitationLength > 0 && court.minCitationLength > form.citeNo.length) {
                errors += `${errors.length > 0 ? '; ' : ''}The ${getCitationLabel(court)} must be at least ${court.minCitationLength} characters long`;
            }
        } else if (form.licenseLookupNumber && form.licenseLookupNumber.length > 0) {
            dob = null;
        } else if (form.firstName || form.lastName) {
            if (!form.firstName || !form.lastName) errors += `${errors.length > 0 ? '; ' : ''}A name search requires your first and last name`;
        }
    }
    if (!court.noDobLookup && !dob && !court.zipLookups && !form.licenseLookupNumber) {
        errors += `${errors.length > 0 ? '; ' : ''}${court.courtName} requires a party's date of birth to search for balances`;
    } else if (court.zipLookups && !form.zip && !form.licenseLookupNumber) {
        errors += `${errors.length > 0 ? '; ' : ''}${court.courtName} requires a party's zip code to search for balances`;
    }
    if (dob && !isValid(new Date(dob))) {
        errors += `${errors.length > 0 ? '; ' : ''}Please enter a valid date of birth`;
    }
    return errors;
}

export const getBalanceQuery = (court, form, dob) => {
    const query = {};
    query.courtId = court.courtId;
    if (form.citeNo && form.citeNo.length) {
        const citeNo = form.citeNo.replace(/%/g, '');
        query.citationNumber = citeNo;
    } else if (form.licenseLookupNumber && form.licenseLookupNumber.length) {
        const licenseLookupNumber = form.licenseLookupNumber.replace(/%/g, '');
        query.licensePlateNumber = licenseLookupNumber;
    } else if (form.lastName && form.lastName.length) {
        query.firstName = form.firstName;
        query.lastName = form.lastName;
        if (dob) {
            query.dob = formatISO(dob, { representation: "date" });
        }
    } else {
        const caseNo = form.caseNo.replace(/%/g, '');
        query.caseNumber = caseNo;
    }
    if (dob && !form.licenseLookupNumber && !court.zipLookups) {
        query.dob = formatISO(dob, { representation: "date" });
    } else if (form.zip && court.zipLookups) {
        query.zip = form.zip;
    }
    query.lookupCaseOnly = false;
    return JSON.stringify(query);
}

export const getFeeQuery = (court, courtAmt) => {
    const query = {
        courtId: court.courtId,
        paymentCategory: 'online',
        paymentType: 'card',
        courtAmount: courtAmt
    }
    return JSON.stringify(query);
}

export const toPaymentBalance = (balance, court) => {
    if (balance.ineligibleForPayment && !balance.bondId && !balance.trafficSchoolId) return null;
    const paymentPlanId = balance.paymentPlanId;
    let pymtAmt = balance.partialAmt && balance.partialAmt > 0 ? balance.partialAmt : balance.courtAmount;
    let partialPayment = false;
    let partialFee;
    if (balance.partialAmt && balance.partialAmt > 0 && balance.partialAmt < balance.courtAmount) {
        partialPayment = true;
        partialFee = balance.partialPaymentFee && balance.partialPaymentFee > 0 ? balance.partialPaymentFee : 0;
        if (partialFee > 0) pymtAmt = parseFloat(getDecimal(pymtAmt + partialFee, false));
    } else partialFee = null;
    const out = {
        refId: balance.refId,
        description: balance.description,
        courtAmount: balance.courtAmount,
        balancePaymentAmount: pymtAmt,
        partialPayment: partialPayment,
        partialPaymentFee: partialFee,
        partialAmt: balance.partialAmt,
        partyName: balance.party.partyName,
        partyId: balance.party.id,
        caseId: balance.caseId,
        chargeId: balance.chargeId,
        caseNumber: balance.caseNumber,
        citationNumber: balance.citationNumber,
        licensePlateNumber: balance.licensePlateNumber,
        balanceType: balance.balanceType,
        baseBalanceType: balance.baseBalanceType,
        dob: balance.party.dob,
        minimumPartialAmount: balance.minimumPartialAmount,
        paymentPlanId: paymentPlanId,
        bondId: balance.bondId,
        serviceFee: null,
        disposed: balance.disposed,
        ineligible: balance.ineligibleForPayment,
        violationDate: balance.violationDate
    };
    if (balance.trafficSchoolId) {
        out.trafficSchoolId = balance.trafficSchoolId;
        out.trafficSchoolType = balance.trafficSchoolType;
        out.optionCompletionText = balance.trafficSchoolOptions.find(o => o.optionId === balance.trafficSchoolId)?.optionCompletionText || null;
        out.balancePaymentAmount = getTrafficSchoolAmount(balance, court);
        out.description += ` (${getTrafficSchoolDescription(balance, court)})`
    } else if (out.bondId && balance.bondAmt && balance.bondAmt > 0) {
        out.balancePaymentAmount = balance.bondAmt;
        out.description = `Bond for: ${balance.description}`;
    }

    const serviceFee = getBalanceServiceFee(balance);
    if (serviceFee > 0) {
        out.balancePaymentAmount = parseFloat(getDecimal(out.balancePaymentAmount + serviceFee, false));
        out.serviceFee = serviceFee;
    }
    return out;
}

export const getCaseLabel = (court) => {
    if (!court) return 'Case Number';
    if (court.caseNumberLabel) return court.caseNumberLabel;
    if (court.nonCourtOrganization) return 'Account Number';
    return 'Case Number';
}

export const getCitationLabel = (court) => {
    if (!court) return 'Citation Number';
    if (court.citationNumberLabel) return court.citationNumberLabel;
    if (court.nonCourtOrganization) return 'Reference Number';
    return 'Citation Number';
}
export const getLicenseLabel = (court) => {
    if (court && court.licensePlateLookup) {
        return 'License Plate Number';
    } else {
        return '';
    }
}

const getTrafficSchoolAmount = (balance, court) => {
    const opts = balance.trafficSchoolOptions.filter(o => o.optionId === balance.trafficSchoolId);
    if (opts && opts.length > 0) {
        const configs = court.trafficSchoolConfigs.filter(c => c.optionType === opts[0].optionType);
        if (configs && configs.length > 0 && configs[0].requireBalanceFees) {
            return parseFloat(getDecimal(balance.courtAmount + opts[0].optionAmount, false));
        } else {
            return opts[0].optionAmount;
        }
    }
    return balance.courtAmount;
}

const getTrafficSchoolDescription = (balance, court) => {
    const opts = balance.trafficSchoolOptions.filter(o => o.optionId === balance.trafficSchoolId);
    if (opts && opts.length > 0) {
        if (opts[0].optionDescription) return opts[0].optionDescription;
        const configs = court.trafficSchoolConfigs.filter(c => c.optionType === opts[0].optionType);
        if (configs && configs.length > 0 && configs[0].optionDescription) return configs[0].optionDescription;
    }
    return 'Traffic School';
}
export const getPartialPaymentValidation = (court, displayData) => {
    if (court.acceptPartials && displayData.balances!==null) {
        return displayData.balances.some(b => b.disposed && !b.noPartialPayment &&
        !b.ineligibleForPayment && b.courtAmount > 0)
    }
    return false;
}


export const getTrafficSchoolText = (balances, court) => {
    const bals = balances.filter(b => b.trafficSchoolType);
    const configs = court.trafficSchoolConfigs.filter(c => c.optionType === bals[0].trafficSchoolType);
    return configs[0].agreementText;
}

const getBalanceServiceFee = (balance) => {
    if (balance.paymentPlan && balance.paymentPlan.planPaymentServiceFee && balance.paymentPlan.planPaymentServiceFee > 0) {
        return balance.paymentPlan.planPaymentServiceFee;
    } else if (balance.paymentPlans && balance.paymentPlans.length > 0) {
        let fee = 0;
        balance.paymentPlans.forEach(p => {
            if (p.planPaymentServiceFee && p.planPaymentServiceFee > 0 && fee <= 0) {
                fee = p.planPaymentServiceFee;
            }
        });
        fee = parseFloat(getDecimal(fee, false));
        if (fee > 0.00) {
            return fee;
        }
    }
    return 0;
}

export const toPaidPlan = (planIn) => {
    const planOut = {
        planId: planIn.planId,
        planDescription: planIn.planDescription,
        planPaymentAmount: 0.00,
        planPaymentServiceFee: planIn.planPaymentServiceFee,
        installments: [],
        installmentCount: 0,
        balances: [],
        cases: getPlanCases(planIn)
    }
    if (planIn.installments) {
        planIn.installments.forEach(i => {
            if (i.checked) {
                planOut.planPaymentAmount += i.installmentAmount
                planOut.installments.push(Object.assign({}, i));
            }
        });
        if (planIn.planPaymentServiceFee) {
            planOut.planPaymentAmount += planIn.planPaymentServiceFee;
        }
        planOut.planPaymentAmount = parseFloat(getDecimal(planOut.planPaymentAmount, false));
        if (planIn.balances && planOut.planPaymentAmount > 0.00) {
            let balanceAmt = 0.00;
            planIn.balances.forEach((b, idx) => {
                if (balanceAmt < planOut.planPaymentAmount) {
                    const balOut = Object.assign({}, b);
                    balOut.description = `Payment plan: ${balOut.description}`;
                    if (((balOut.balancePaymentAmount + balanceAmt) > planOut.planPaymentAmount) || idx === (planIn.balances.length - 1)) {
                        balOut.balancePaymentAmount = planOut.planPaymentAmount - balanceAmt;
                    }
                    balOut.balancePaymentAmount = parseFloat(getDecimal(balOut.balancePaymentAmount, false));
                    balanceAmt += balOut.balancePaymentAmount;
                    planOut.balances.push(balOut);
                }
            })
        }
    }
    return planOut;
}

export const getCourtAmount = (selectedBals, selectedPlans) => {
    let total = 0;
    if (selectedBals && selectedBals.length > 0) {
        selectedBals.forEach(b => {
            total = total + b.balancePaymentAmount;
            total = parseFloat(getDecimal(total, false));
        });
    }
    if (selectedPlans && selectedPlans.length > 0) {
        selectedPlans.forEach(p => {
            total = total + p.planPaymentAmount
            total = parseFloat(getDecimal(total, false));
        });
    }
    return total;
}

export const validatePaymentAmounts = (selectedBalances, selectedPlans, feeResponse) => {
    let error = '';
    let detailsTotal = 0.00;
    if (selectedBalances) {
        detailsTotal = selectedBalances.reduce((sum, balance) => sum + (balance.balancePaymentAmount || 0), 0);
    }
    if (selectedPlans) {
        const plansTotal = selectedPlans.reduce((sum, plan) => sum + (plan.planPaymentAmount || 0), 0);
        detailsTotal += plansTotal;
    }
    detailsTotal = parseFloat(getDecimal(detailsTotal, false));
    if (detailsTotal !== feeResponse.courtAmount) {
        error = 'The payment amount does not match the total of the selected balances.  Please clear the shopping cart and re-select the item(s) you wish to pay.';
    }
    return error;
}

export const getPaymentRequest = (court, selectedBalances, selectedPlans, feeResponse, bondOptionalAgreed, displayBalances, optionFormData) => {
    const serviceFee = getServiceFee(selectedBalances, selectedPlans);
    const request = {
        courtId: court.courtId,
        paymentDescription: `Payment for: ${selectedBalances.length > 0 ? selectedBalances[0].caseNumber : selectedPlans[0].planDescription}`,
        partyName: selectedBalances.length > 0 ? selectedBalances[0].partyName : selectedPlans[0].balances[0].partyName,
        courtAmount: feeResponse.courtAmount,
        feeAmount: feeResponse.citepayFee,
        totalAmount: feeResponse.totalAmount,
        serviceFee: serviceFee > 0 ? serviceFee : null,
        processingType: 'online',
        lookup: true,
        paymentCategory: 'online',
        paymentLocation: 'Online',
        sourceApp: 'Mobile',
        balances: selectedBalances,
        paymentPlans: selectedPlans,
        unpaidBalances: getUnpaidBalances(displayBalances)
    };
    if (showBond(selectedBalances, court) && court.bondYesno) {
        request.bondOptionalAgreed = bondOptionalAgreed;
    }
    if (optionFormData) request.optionForm = JSON.parse(JSON.stringify(optionFormData));
    return request;
}

export const getServiceFee = (balances, plans) => {
    let serviceFee = 0;
    if (balances && balances.length > 0) {
        balances.forEach(b => {
            if (b.serviceFee && b.serviceFee > 0) {
                serviceFee += b.serviceFee;
            }
        })
    }
    if (plans && plans.length > 0) {
        plans.forEach(p => {
            if (p.planPaymentServiceFee && p.planPaymentServiceFee > 0) {
                serviceFee += p.planPaymentServiceFee;
            }
        })
    }
    return parseFloat(getDecimal(serviceFee, false));
}

const getUnpaidBalances = (displayBalances) => {
    const unpaid = displayBalances.filter(b => !b.selected || b.partialAmt);
    const out = [];
    unpaid.forEach(b => {
        const amt = parseFloat(getDecimal(b.partialAmt ? b.courtAmount - b.partialAmt : b.courtAmount, false));
        const unpaidBalance = {
            key: b.refId,
            unpaidAmount: amt,
            violationDate: b.violationDate ? dateIso2Us(b.violationDate) : '',
            description: b.description,
            caseNumber: b.caseNumber,
            citationNumber: b.citationNumber
        };
        out.push(unpaidBalance);
    })
    return out;
}

export const validateAddBalance = (balance, selectedBals, selectedPlans, court) => {
    const nextItem = '; ';
    let errors = '';
    const minimumPartialAmount =
        balance.minimumPartialAmount !== null
            ? balance.minimumPartialAmount
            : court.minimumPartial;

    if (balance.partialAmt >= 0) {
        if (balance.partialAmt === 0) {
            errors = 'Value must be greater than $0';
        } else if (balance.noPartialPayment || (court.acceptPartials !== null && !court.acceptPartials && !balance.forceAllowPartialPayment)) {
            errors = 'Partial payments are not allowed';
        } else if (minimumPartialAmount > 0 && balance.balancePaymentAmount < minimumPartialAmount) {
            errors = `A partial payment for this balance must be at least $${getDecimal(minimumPartialAmount, false)}`;
        }
    }
    if (balance.partialAmt > balance.courtAmount) {
        errors = `A partial payment for this balance may not exceed the outstanding balance amount of $${getDecimal(balance.courtAmount, true)}`;
    }
    if (selectedPlans && selectedPlans.length > 0) {
        selectedPlans.forEach(p => {
            if (p.cases && p.cases.includes(balance.caseNumber)) {
                if (errors) errors += nextItem;
                errors += `${court.nonCourtOrganization ? 'Account ' : 'Case '}${balance.caseNumber} cannot be included in a payment plan and paid separately`;
            }
        });
    }
    if (balance.trafficSchoolId) {
        const tsBals = selectedBals.filter(b => b.trafficSchoolId);
        if (tsBals && tsBals.length > 0) {
            if (errors) errors += nextItem;
            errors += 'Only one traffic school option may be selected';
        }
    }

    return errors;
};

export const validateAddPlan = (plan, selectedBals, selectedPlans, court) => {
    const nextItem = '; ';
    let errors = '';
    if (!plan.installments || plan.installments.filter(i => i.checked).length < 1) {
        errors = 'You must check at least one installment to make a payment on a payment plan';
    } else {
        let lastSelected = 0;
        plan.installments.forEach((i) => {
            if (i.checked) {
                if (i.priorityOrder - lastSelected > 1) {
                    if (errors) errors += nextItem;
                    errors += 'Installments on a payment plan must be paid in the order displayed';
                }
                lastSelected = i.priorityOrder;
            }
        })
    }
    if (selectedBals && selectedBals.length > 0) {
        selectedBals.forEach(b => {
            if (plan.cases && plan.cases.includes(b.caseNumber)) {
                if (errors) errors += nextItem;
                errors += `${court.nonCourtOrganization ? 'Account ' : 'Case '}${b.caseNumber} may not be included in a payment plan and paid separately`;
            }
        })
    }
    if (selectedPlans && selectedPlans.length > 0) {
        selectedPlans.forEach(p => {
            if (p.cases) {
                const caseArray = p.cases.split(',');
                caseArray.forEach(c => {
                    if (plan.cases && plan.cases.includes(c)) {
                        if (errors) errors += nextItem;
                        errors += `${court.nonCourtOrganization ? 'Account ' : 'Case '}${c} may only be included in one payment plan in a payment`;
                    }
                })
            }
        })
    }
    return errors;
}

export const showTrafficSchool = (balances, court) => {
    const tsBals = balances.filter(b => b.trafficSchoolType && b.trafficSchoolType.length > 0);
    if (tsBals && tsBals.length > 0 && court.trafficSchoolConfigs) {
        const configs = court.trafficSchoolConfigs.filter(c => c.optionType === tsBals[0].trafficSchoolType);
        if (configs.length > 0 && configs[0].agreementText && configs[0].agreementText.length > 0) return true;
    }
    return false;
}

export const showPlea = (balances, court) => {
    if (court.plea && court.plea.length > 0) {
        const plBals = balances.filter(b => (!b.disposed && !b.ineligible && !b.trafficSchoolType && !b.bondId));
        if (plBals && plBals.length > 0) {
            return true;
        }
    }
    return false;
}

export const showBond = (balances, court) => {
    if (court.bondAgree && court.bondAgree.length > 0) {
        const bondBals = balances.filter(b => b.bondId);
        return (bondBals && bondBals.length > 0);
    }
    return false;
}

export const validatePayment = (displayData, selectedBals, court, feeResponse) => {
    let errors = '';
    if (court.maxTransactionLimit && court.maxTransactionLimit > 0) {
        if (feeResponse.courtAmount > court.maxTransactionLimit) {
            errors += (errors.length > 0 ? '; ' : '') + 'The total amount for the selected balances exceeds the maximum payment amount allowed by ' + court.courtName;
        }
    }
    const undisposedErrors = checkUndisposed(displayData, selectedBals, court);
    if (undisposedErrors) errors += (errors.length > 0 ? '; ' : '') + undisposedErrors;
    const checkDisposedError = checkDisposed(displayData, selectedBals, court);
    if (checkDisposedError) errors += (errors.length > 0 ? '; ' : '') + checkDisposedError;
    return errors;

}

const checkUndisposed = (displayData, selectBals, court) => {
    let errors = '';
    if (court.payAllUndisposed || court.noIneligibleUndisposed) {
        const retrievedBals = displayData.balances.filter(bal => !bal.disposed);
        if (retrievedBals) {
            const cases = {};
            retrievedBals.forEach(bal => {
                const caseId = `${bal.caseId}`;
                if (!cases[caseId]) cases[caseId] = [];
                cases[caseId].push(bal);
            })
            for (let caseId in cases) {
                if (!errors && cases[caseId].length > 1) {
                    const selected = selectBals.filter(sb => sb.caseId === parseInt(caseId));
                    if (selected && selected.length > 0) {
                        const caseBals = cases[caseId];
                        caseBals.every(bal => {
                            if (bal.bonds && bal.bonds.length > 0) {
                                return true;
                            }
                            if (court.noIneligibleUndisposed && bal.ineligibleForPayment) {
                                errors = `No pending balances for case ${bal.caseNumber} may be paid because a balance in the case is not eligible for payment`;
                                return false;
                            }
                            if (court.payAllUndisposed && !bal.selected) {
                                errors = `All pending balances for case ${bal.caseNumber} must be paid in order to pay any of them`;
                                return false;
                            }
                            return true;
                        });
                    }
                }
            }
        }
    }
    return errors;
};

const checkDisposed = (displayData, selectBals, court) => {
    let errors = '';
    const context = court.priorityBalanceWarning || `Non-pending balances ${court.priorityAcrossCases ? 'within a case ' : ''}must be paid in the priority order specified by the court; please pay them in the order listed to avoid this error`;

    if (court.usePriorityBalances) {
        const retrievedBals = displayData.balances.filter(bal => bal.disposed && !bal.ineligibleForPayment);

        if (retrievedBals.length > 0) {
            if (court.priorityAcrossCases) {
                const sortedBals = [...retrievedBals].sort((a, b) => (a.priorityOrder || 9999) - (b.priorityOrder || 9999));
                let lowestUnpaid = 9999;

                for (let bal of sortedBals) {
                    if (!bal.selected) {
                        lowestUnpaid = bal.priorityOrder || 9999;
                        break;
                    }
                }

                let partialPriority = 9999;
                for (let bal of sortedBals) {
                    if (bal.selected && bal.priorityOrder > lowestUnpaid) {
                        errors = context.replace('@CASE@', 'your selected balances');
                        break;
                    }
                    if (bal.selected && bal.partialAmt && bal.partialAmt > 0) {
                        if (bal.priorityOrder < partialPriority) {
                            partialPriority = bal.priorityOrder;
                        }
                    }
                    if (bal.selected && bal.priorityOrder > partialPriority) {
                        errors = 'Higher priority balances must be paid in full before any amount can be paid on lower priority balances; please allocate partial payments to the highest priority balance(s) to avoid this error';
                        break;
                    }
                }
            } else {
                const cases = {};
                retrievedBals.forEach(bal => {
                    const caseId = `${bal.caseId}`;
                    if (!cases[caseId]) cases[caseId] = [];
                    cases[caseId].push(bal);
                });

                for (let caseId in cases) {
                    const caseBals = cases[caseId];
                    const sortedBals = [...caseBals].sort((a, b) => (a.priorityOrder || 9999) - (b.priorityOrder || 9999));
                    let lowestUnpaid = 9999;

                    for (let bal of sortedBals) {
                        if (!bal.selected) {
                            lowestUnpaid = bal.priorityOrder || 9999;
                            break;
                        }
                    }

                    let partialPriority = 9999;
                    for (let bal of sortedBals) {
                        if (bal.selected && bal.priorityOrder > lowestUnpaid) {
                            errors = context.replace('@CASE@', bal.caseNumber);
                            break;
                        }
                        if (bal.selected && bal.partialAmt && bal.partialAmt > 0) {
                            if (bal.priorityOrder < partialPriority) {
                                partialPriority = bal.priorityOrder;
                            }
                        }
                        if (bal.selected && bal.priorityOrder > partialPriority) {
                            errors = 'Higher priority balances within a case must be paid in full before any amount can be paid on lower priority balances; please allocate partial payments to the highest priority balance(s) to avoid this error';
                            break;
                        }
                    }

                    if (errors) {
                        break;
                    }
                }
            }
        }
    }

    return errors;
};


const getPlanCases = (plan) => {
    const caseArray = [];
    plan.balances.forEach(b => caseArray.push(b.caseNumber));
    return  caseArray.join();
}

const getPartialAmount = (balances) => {
    let partialAmt = 0;
    if (balances && balances.length > 0) {
        balances.forEach(b => {
            if (b.partialAmt && b.partialAmt > 0) partialAmt += b.balancePaymentAmount;
        });
        partialAmt = parseFloat(getDecimal(partialAmt, false));
    }
    return partialAmt;
}
